/**
 * ContentFilterCard Component
 *
 * Filter controls for the content selection modal.
 * Provides search, schema status filter, and bulk selection controls.
 */

import { Input, Select, Button, Space } from "antd";
import { Search } from "lucide-react";
import type { FAQContentType } from "@/types/schema";

interface ContentFilterCardProps {
	schemaType: "article" | "faq";
	contentType?: FAQContentType;
	filters: {
		search?: string;
		schemaStatus?: string;
	};
	onFiltersChange: (filters: { search?: string; schemaStatus?: string }) => void;
	selectedCount: number;
	onSelectPage: () => void;
	onSelectAll: () => void;
	onClearSelection: () => void;
}

export function ContentFilterCard({
	filters,
	onFiltersChange,
	selectedCount,
	onSelectPage,
	onSelectAll,
	onClearSelection,
}: ContentFilterCardProps) {
	return (
		<div className="flex flex-col gap-3 p-4 bg-muted rounded-lg mb-4">
			<div className="flex items-center justify-between gap-4 flex-wrap">
				{/* Left side: Search and Filter */}
				<Space size="middle" wrap>
					<Input
						placeholder="Search by title..."
						prefix={<Search size={16} className="text-muted-foreground" />}
						value={filters.search || ""}
						onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
						style={{ width: 240 }}
						allowClear
					/>

					<Select
						value={filters.schemaStatus || "all"}
						onChange={(value) => onFiltersChange({ ...filters, schemaStatus: value })}
						style={{ width: 180 }}
						options={[
							{ value: "all", label: "All Items" },
							{ value: "needs_schema", label: "Needs Schema" },
							{ value: "has_schema", label: "Has Schema" },
							{ value: "needs_article", label: "Needs Article" },
							{ value: "needs_faq", label: "Needs FAQ" },
						]}
					/>
				</Space>

				{/* Right side: Selection controls */}
				<Space size="small">
					<Button size="small" onClick={onSelectPage}>
						Select Page
					</Button>
					<Button size="small" onClick={onSelectAll}>
						Select All
					</Button>
					{selectedCount > 0 && (
						<Button size="small" onClick={onClearSelection}>
							Clear ({selectedCount})
						</Button>
					)}
				</Space>
			</div>
		</div>
	);
}
